import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Shield, Sparkles, Lock } from "lucide-react";
import { CODE_TO_GEOJSON_NAMES, statusColor, statusFill } from "../lib/provinces";

const FitToIndonesia = () => {
  const map = useMap();
  useEffect(() => {
    map.fitBounds([
      [-11, 94],
      [6, 141],
    ]);
  }, [map]);
  return null;
};

const buildNameToCode = () => {
  const m = {};
  Object.entries(CODE_TO_GEOJSON_NAMES).forEach(([code, names]) => {
    names.forEach((n) => {
      if (!m[n]) m[n] = code;
    });
  });
  return m;
};

const NAME_TO_CODE = buildNameToCode();

export const MapPanel = ({
  heatmap,
  national,
  nationalStatus,
  onAnalisisClick,
  onProvinceClick,
  children,
}) => {
  const [geojson, setGeojson] = useState(null);

  useEffect(() => {
    fetch("/indonesia-provinces.json")
      .then((r) => r.json())
      .then(setGeojson)
      .catch(() => setGeojson(null));
  }, []);

  const codeStatus = React.useMemo(() => {
    const out = {};
    (heatmap || []).forEach((p) => {
      out[p.code] = p;
    });
    return out;
  }, [heatmap]);

  const styleFn = (feature) => {
    const rawName = (feature.properties?.Propinsi || feature.properties?.name || "").toUpperCase();
    const code = NAME_TO_CODE[rawName];
    const info = code ? codeStatus[code] : null;
    const status = info?.status?.toLowerCase() || "aman";
    return {
      color: statusColor(status),
      weight: 0.8,
      opacity: 0.9,
      fillColor: statusColor(status),
      fillOpacity: status === "aman" ? 0.55 : 0.7,
    };
  };

  const onEachFeature = (feature, layer) => {
    const rawName = (feature.properties?.Propinsi || "").toUpperCase();
    const code = NAME_TO_CODE[rawName];
    const info = code ? codeStatus[code] : null;
    if (info) {
      layer.bindTooltip(
        `<div class="map-tip"><div class="tip-name">${info.name}</div>` +
          `<div class="tip-row"><span>Skor</span><strong style="color:${statusColor(info.status)}">${info.score}</strong></div>` +
          `<div class="tip-row"><span>Status</span><strong style="color:${statusColor(info.status)}">${info.status}</strong></div>` +
          `<div class="tip-hint">Klik untuk ringkasan</div></div>`,
        { sticky: true, direction: "top" }
      );
    }
    if (layer.options) {
      layer.options.dataTestid = `province-${code || rawName}`;
    }
    // Set data-testid on the actual SVG path once Leaflet renders it
    layer.on("add", (e) => {
      const el = e.target?.getElement?.();
      if (el && code) el.setAttribute("data-testid", `province-${code}`);
      if (el) el.style.cursor = "pointer";
    });
    layer.on("click", () => {
      if (info && onProvinceClick) {
        onProvinceClick({ code, name: info.name, status: info.status, score: info.score });
      }
    });
    layer.on("mouseover", (e) => {
      e.target.setStyle({ weight: 2.2, fillOpacity: 0.9 });
    });
    layer.on("mouseout", (e) => {
      e.target.setStyle({ weight: 0.8, fillOpacity: (info?.status || "aman").toLowerCase() === "aman" ? 0.55 : 0.7 });
    });
  };

  const natColor = statusColor(nationalStatus);

  return (
    <div className="nms-map-wrap" data-testid="map-panel">
      <MapContainer
        center={[-2.5, 118]}
        zoom={5}
        scrollWheelZoom={false}
        zoomControl={false}
        dragging={false}
        doubleClickZoom={false}
        attributionControl={false}
        className="nms-leaflet"
        style={{ height: "100%", width: "100%", background: "var(--nms-bg-1)" }}
      >
        <TileLayer
          attribution=""
          url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
        />
        {geojson && (
          <GeoJSON data={geojson} style={styleFn} onEachFeature={onEachFeature} />
        )}
        <FitToIndonesia />
      </MapContainer>

      {/* National AMAN pill — top center */}
      <div
        className="map-overlay map-pill"
        style={{
          background: `linear-gradient(135deg, ${natColor}, ${natColor}cc)`,
          boxShadow: `0 8px 22px ${natColor}55, inset 0 0 0 1px ${natColor}`,
        }}
        data-testid="national-pill"
      >
        <span className="pill-text" data-testid="national-status">
          {(nationalStatus || "—").toUpperCase()}
        </span>
        <Shield size={14} className="pill-ico" />
      </div>

      {/* National score badge (small floating) */}
      <div className="map-overlay map-score" data-testid="national-score-wrap">
        <span className="ms-lbl">INDEKS NASIONAL</span>
        <span className="ms-val" style={{ color: natColor }} data-testid="national-score">
          {national ?? "—"}
        </span>
      </div>

      {/* Top right analisis button */}
      <button
        type="button"
        className="map-overlay map-analisis"
        onClick={onAnalisisClick}
        data-testid="analisis-rekomendasi-btn"
      >
        <Sparkles size={14} className="ar-ico" />
        <div className="ar-text">
          <span>ANALISIS</span>
          <strong>REKOMENDASI</strong>
        </div>
      </button>

      {/* Bottom-left brand */}
      <div className="map-overlay map-brandline">
        <span className="bl-dot" /> NMS GEOSPATIAL INTELLIGENCE
      </div>

      {/* Bottom-right lock */}
      <div className="map-overlay map-lock" aria-hidden>
        <Lock size={14} />
      </div>

      {/* Legend */}
      <div className="map-overlay map-legend" data-testid="map-legend">
        <span><i style={{ background: "#10b981" }} /> Aman</span>
        <span><i style={{ background: "#facc15" }} /> Siaga</span>
        <span><i style={{ background: "#f59e0b" }} /> Waspada</span>
        <span><i style={{ background: "#ef4444" }} /> Kritis</span>
      </div>

      {children}
    </div>
  );
};

export default MapPanel;
